"use client";

import * as React from "react";
import { PlusIcon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { AnalyticsEvents, trackEvent } from "@/lib/analytics/index";
import { toFaqPageJsonLd } from "@/lib/faq-schema";

import type { FAQItem } from "./FAQAccordionSection";

type Props = {
  faqs: FAQItem[];
  analyticsLocation?: string;
  title?: string;
  isRtl?: boolean;
  /* Set false only when the page already emits its own FAQPage node for this
     same list - two of them on one page is worse than none. */
  emitSchema?: boolean;
};

export default function FAQAccordionSectionClient({
  faqs,
  analyticsLocation = "Homepage FAQ",
  title = "Frequently asked questions",
  isRtl = false,
  emitSchema = true,
}: Props) {
  // Fires on the summary click, before the <details> flips, so `open` still
  // holds the old state. A default-open item never counts.
  const trackOpen = React.useCallback(
    (faq: FAQItem, e: React.MouseEvent<HTMLElement>) => {
      const details = e.currentTarget.parentElement as HTMLDetailsElement | null;
      if (details?.open) return;
      trackEvent(AnalyticsEvents.FAQ_OPENED, {
        question: faq.question,
        location: analyticsLocation,
      });
    },
    [analyticsLocation]
  );

  /* The FAQPage node lives here rather than in each caller, so every page that
     renders this accordion ships the markup. */
  const faqJsonLd = React.useMemo(() => {
    if (!emitSchema) return null;
    return toFaqPageJsonLd(
      faqs.map((f) => ({ question: f.question, answer: f.answer }))
    );
  }, [emitSchema, faqs]);

  return (
    <section
      id="faq"
      className="flex flex-col w-full max-w-[520px] lg:max-w-[800px] mx-auto items-center gap-10 px-4 sm:px-6 md:px-10 py-16 lg:py-24 justify-center"
    >
      {faqJsonLd ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: faqJsonLd }}
        />
      ) : null}

      <h2 className="font-sans font-medium text-[#15151a] text-[30px] lg:text-[40px] text-center tracking-[-0.03em] leading-[1.1] w-full">
        {title}
      </h2>

      <div className="w-full flex flex-col gap-6">
        {/* Native <details>: every question and answer ships in the server
            HTML and opens without JavaScript (this list used to stop at 5
            behind "Load more"); `name` keeps one open at a time. */}
        {faqs
          .filter((faq) => Boolean(faq.answer))
          .map((faq) => (
            <details
              key={faq.id}
              name="faq"
              open={faq.defaultOpen || undefined}
              className="group rounded-2xl border border-solid border-black/[0.06] bg-white px-10 py-6 open:bg-black/[0.03]"
            >
              <summary
                className={`w-full flex cursor-pointer list-none items-start justify-between gap-4 [&::-webkit-details-marker]:hidden ${
                  isRtl ? "text-right" : "text-left"
                }`}
                onClick={(e) => trackOpen(faq, e)}
              >
                <span className="font-sans font-medium text-black text-base tracking-[-0.32px] leading-[22.4px]">
                  {faq.question}
                </span>
                <PlusIcon
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-nws-grey-text transition-transform duration-200 group-open:rotate-45"
                />
              </summary>
              <div className="pt-2 font-sans font-normal text-black text-base tracking-[-0.32px] leading-[22.4px] faq-markdown">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, children }) => {
                      const isExternal = /^https?:\/\//.test(href ?? "");
                      return (
                        <a
                          href={href}
                          className="underline text-blue-600 hover:text-blue-800"
                          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        >
                          {children}
                        </a>
                      );
                    },
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-3">
                        <table className="min-w-full border border-nws-grey-medium text-sm">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => <thead className="bg-nws-grey-100">{children}</thead>,
                    th: ({ children }) => (
                      <th className="border border-nws-grey-medium px-3 py-2 text-left font-medium">{children}</th>
                    ),
                    td: ({ children }) => (
                      <td className="border border-nws-grey-medium px-3 py-2">{children}</td>
                    ),
                    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  }}
                >
                  {faq.answer}
                </ReactMarkdown>
              </div>
            </details>
          ))}
      </div>
    </section>
  );
}
